import { ReactNode, MouseEventHandler } from 'react'

type CardVariant = 'default' | 'elevated' | 'bordered'
type CardPadding = 'none' | 'sm' | 'md' | 'lg'

export interface CardProps {
  children: ReactNode
  className?: string
  title?: string
  onClick?: MouseEventHandler<HTMLDivElement>
  variant?: CardVariant
  clickable?: boolean
  padding?: CardPadding
}

interface CardHeaderProps {
  children: ReactNode
  className?: string
}

interface CardBodyProps {
  children: ReactNode
  className?: string
}

// Styles de base
const baseStyles = 'bg-white rounded-lg'

// Variantes (CDC section 4.6)
const variantStyles: Record<CardVariant, string> = {
  default: 'shadow-card border border-gray-100',
  elevated: 'shadow-card-hover',
  bordered: 'border border-gray-200',
}

// Style cliquable
const clickableStyles = 'cursor-pointer transition-all duration-200 hover:shadow-card-hover hover:border-gray-300'

// Paddings
const paddingStyles: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
}

export function Card({
  children,
  className = '',
  title,
  onClick,
  variant = 'default',
  clickable = false,
  padding,
}: CardProps) {
  const variantClass = variantStyles[variant]
  const clickableClass = clickable || onClick ? clickableStyles : ''
  const paddingClass = padding ? paddingStyles[padding] : ''

  return (
    <div
      className={`${baseStyles} ${variantClass} ${clickableClass} ${paddingClass} ${className}`.trim()}
      onClick={onClick}
    >
      {title && (
        <div className="card-header">
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
      )}
      {children}
    </div>
  )
}

export function CardHeader({ children, className = '' }: CardHeaderProps) {
  return (
    <div className={`card-header ${className}`}>
      {children}
    </div>
  )
}

export function CardBody({ children, className = '' }: CardBodyProps) {
  return (
    <div className={`card-body ${className}`}>
      {children}
    </div>
  )
}

export default Card
